/**
 * Bedrock CTA styling — a pure function of variant + size + tone.
 *
 * Split from `Cta.tsx` for the same reason `marketing-ui/button-classes.ts` is
 * split from its button: a react-router `<Link>` cannot be a `<button>` or an
 * `<a>`, so call sites spread these classes onto the Link directly. Keeping the
 * styling in its own module also keeps `Cta.tsx` a components-only file, which
 * is what Fast Refresh needs to hot-reload it.
 */
export type CtaVariant = 'primary' | 'secondary' | 'ghost';
export type CtaSize = 'sm' | 'md';

/** Minimum 44×44 touch target (`.claude/rules/ui-design.md` §6 house rule). */
const SIZE: Record<CtaSize, string> = {
  sm: 'min-h-11 px-[17px] text-sm rounded-[10px] gap-2',
  md: 'min-h-[52px] px-[26px] text-[0.97rem] rounded-xl gap-2.5',
};

/**
 * The fourth state § 3 names, and the one that had no treatment at all.
 *
 * A `<Cta disabled>` rendered pixel-identical to an enabled one — and worse,
 * `:hover` still applies to a disabled button in CSS, so the contact form's
 * submit LIT UP under the pointer while it was refusing to be pressed. That is
 * not merely a missing cue; it is an affordance actively asserting the opposite
 * of the truth, at the one moment (mid-submit) when a user is deciding whether
 * to click again.
 *
 * Locked fill + `cursor-not-allowed` + opacity, in that order of importance.
 * Tailwind orders `disabled:` AFTER `hover:` and `active:`, so pinning the
 * resting value here is what actually silences both — opacity alone would have
 * left the fill still moving, just more faintly.
 *
 * 60% is not an arbitrary fade: the light primary composites to `#6d6d6e`,
 * which keeps its white label at 5.17:1. WCAG 1.4.3 exempts inactive controls
 * from the contrast floor; clearing it anyway means a disabled button is still
 * READ rather than merely seen, which is the difference between "not yet" and
 * "broken".
 */
const DISABLED_BASE = 'disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none';

/**
 * Hover lifts the fill; ACTIVE takes it past resting in the other direction, so
 * a press reads as the control going down rather than as the hover simply
 * ending.
 *
 * There was no press state anywhere on this site — measured, zero `active:`
 * utilities in the whole app. On a mouse that is a small thing, because hover
 * already says the control is live. On TOUCH there is no hover at all, so a tap
 * got no feedback whatsoever between the finger landing and the page changing,
 * which is the interval a user is most likely to think nothing happened and tap
 * again. `ui-design.md` § 3 lists active alongside hover and focus for exactly
 * this reason.
 *
 * Colour only, deliberately: a transform would need a `motion-reduce:` partner
 * and would move the element under the focus ring's offset. A fill change needs
 * neither.
 */
const VARIANT_LIGHT: Record<CtaVariant, string> = {
  primary:
    'bg-[color:var(--bd-ink)] text-white hover:bg-[color:color-mix(in_srgb,var(--bd-ink)_88%,white)] ' +
    'disabled:bg-[color:var(--bd-ink)] ' +
    // Back to the full ink from hover, AND the lift settles. The fill alone
    // was not enough and the measurement said so: `--bd-ink` IS the resting
    // fill, so returning to it showed a press coming off HOVER and nothing at
    // all coming off REST — which is the touch case, the one with no hover to
    // come off. Near-black cannot usefully go darker, so the press is carried
    // by the shadow: the button settles onto the surface.
    'active:bg-[color:var(--bd-ink)] active:shadow-[0_3px_8px_-8px_rgba(12,13,16,.9)] ' +
    'shadow-[0_10px_24px_-16px_rgba(12,13,16,.9)]',
  secondary:
    'border border-[color:var(--bd-line-3)] bg-[color:var(--bd-page)] ' +
    'text-[color:var(--bd-ink)] hover:border-[color:var(--bd-dot)] hover:bg-[color:var(--bd-sunk)] ' +
    'active:bg-[color:color-mix(in_srgb,var(--bd-sunk)_88%,var(--bd-ink))] ' +
    'disabled:border-[color:var(--bd-line-3)] disabled:bg-[color:var(--bd-page)]',
  ghost:
    'text-[color:var(--bd-muted)] hover:bg-[color:var(--bd-sunk)] hover:text-[color:var(--bd-ink)] ' +
    'active:bg-[color:color-mix(in_srgb,var(--bd-sunk)_88%,var(--bd-ink))] ' +
    'disabled:bg-transparent disabled:text-[color:var(--bd-muted)]',
};

const VARIANT_DARK: Record<CtaVariant, string> = {
  primary:
    'bg-white text-[color:var(--bd-ink)] hover:bg-[color:color-mix(in_srgb,var(--bd-page)_91%,var(--bd-ink))] ' +
    'active:bg-[color:color-mix(in_srgb,var(--bd-page)_82%,var(--bd-ink))] disabled:bg-white',
  secondary:
    'border border-[color:var(--bd-band-line-2)] text-[color:var(--bd-band-ink)] ' +
    // Explicit rgba rather than `bg-white/10`: measured under a forced
    // `:active`, the shorthand resolved to `rgba(255,255,255,0)` — the right
    // hue at zero alpha, i.e. invisible. `bg-white/5` resolves correctly in the
    // same build, so this is a quirk of one opacity step in this config rather
    // than of the utility. An arbitrary value cannot be ambiguous.
    'hover:border-white/40 hover:bg-white/5 active:bg-[rgba(255,255,255,0.12)] ' +
    'disabled:border-[color:var(--bd-band-line-2)] disabled:bg-transparent',
  ghost:
    'text-[color:var(--bd-band-body)] hover:bg-white/5 hover:text-[color:var(--bd-band-ink)] ' +
    'active:bg-[rgba(255,255,255,0.12)] ' +
    'disabled:bg-transparent disabled:text-[color:var(--bd-band-body)]',
};

/**
 * `| undefined` on each member is required, not noise: the repo builds with
 * `exactOptionalPropertyTypes`, and `Cta`/`CtaLink` forward their own optional
 * props straight through — so the parameter type has to admit an explicitly
 * passed `undefined`, which a bare `?:` does not.
 */
export interface CtaStyleProps {
  readonly variant?: CtaVariant | undefined;
  readonly size?: CtaSize | undefined;
  readonly onDark?: boolean | undefined;
  readonly className?: string | undefined;
}

export function ctaClasses({
  variant = 'primary',
  size = 'md',
  onDark = false,
  className,
}: CtaStyleProps = {}): string {
  return [
    'inline-flex items-center justify-center font-medium leading-none',
    'transition-colors duration-200',
    // The focus ring is offset so it reads on both grounds without needing a
    // tone-specific colour — the ring is the brand blue on either.
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)] focus-visible:ring-offset-2',
    onDark ? 'focus-visible:ring-offset-[color:var(--bd-band)]' : 'focus-visible:ring-offset-white',
    SIZE[size],
    DISABLED_BASE,
    (onDark ? VARIANT_DARK : VARIANT_LIGHT)[variant],
    className,
  ]
    .filter(Boolean)
    .join(' ');
}
